import type { BillingCycle, SeatPreview, SellablePlan } from "@contract/firm/billing";
import * as React from "react";
import { track } from "../../analytics/analytics";
import * as billing from "../../api/billing";
import { ApiError } from "../../api/client";
import { openCheckout } from "../../billing/paddleCheckout";

/** A change waiting for the admin's yes, with what it costs (both apply at once, prorated). */
type PendingChange = { kind: "upgrade"; cycle: BillingCycle; preview: SeatPreview } | { kind: "seats"; preview: SeatPreview };

const message = (e: unknown, fallback: string) => (e instanceof ApiError || e instanceof Error ? e.message : fallback);

/**
 * The Plan & billing page's actions (spec §7): buy a plan through Paddle's checkout, price
 * and then apply an upgrade or a seat change, and open Paddle's portal (billing details and
 * cancelling). Nothing that charges at once runs without its price shown first.
 */
export function usePlanChange({
  onChanged,
  onUpgraded,
  api = billing,
  checkout = openCheckout,
}: {
  onChanged: () => void;
  onUpgraded: () => void;
  api?: typeof billing;
  checkout?: typeof openCheckout;
}) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState<PendingChange | null>(null);

  const run = async (fallback: string, work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(message(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  /** A new subscription: Paddle's checkout overlay prices and takes payment. */
  const choose = (plan: SellablePlan, cycle: BillingCycle, seats: number) =>
    run("Checkout didn't start. Try again.", async () => {
      const req = { planId: plan, cycle, ...(plan === "team" ? { seats } : {}) };
      const res = await api.startCheckout(req);
      track("web_checkout_started", { plan, cycle, seats: plan === "team" ? seats : 1, upgrade: res.kind === "upgraded" });
      if (res.kind === "upgraded") onUpgraded();
      else await checkout(res);
    });

  const reviewUpgrade = (cycle: BillingCycle, seats: number) =>
    run("We couldn't price that change. Try again.", async () => {
      setPending({ kind: "upgrade", cycle, preview: await api.previewUpgrade({ planId: "team", cycle, seats }) });
    });

  const reviewSeats = (seats: number) =>
    run("We couldn't price that change. Try again.", async () => {
      setPending({ kind: "seats", preview: await api.previewSeats(seats) });
    });

  /** Applies the pending change the admin just saw priced. */
  const confirm = (fromSeats: number) =>
    run("That didn't work. Try again.", async () => {
      if (!pending) return;
      const seats = pending.preview.seats;
      if (pending.kind === "upgrade") {
        const res = await api.startCheckout({ planId: "team", cycle: pending.cycle, seats });
        track("web_checkout_started", { plan: "team", cycle: pending.cycle, seats, upgrade: res.kind === "upgraded" });
        setPending(null);
        // The subscription can end between the preview and the confirm: the server then
        // opens a new checkout instead of upgrading, so only "upgraded" means done.
        if (res.kind === "upgraded") onUpgraded();
        else await checkout(res);
      } else {
        await api.changeSeats(seats);
        track("web_seats_changed", { from: fromSeats, to: seats });
        setPending(null);
        onChanged();
      }
    });

  // The tab opens inside the click (a window opened after an await is popup-blocked in
  // Safari and Firefox) and is pointed at the portal when its URL arrives. Blocked anyway:
  // this tab goes there instead.
  const openPortal = async () => {
    setError(null);
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    try {
      const url = await api.portalUrl();
      track("web_billing_portal_opened");
      if (tab) tab.location.href = url;
      else window.location.assign(url);
    } catch (e) {
      tab?.close();
      setError(message(e, "Couldn't open billing. Try again."));
    }
  };

  return {
    busy,
    error,
    clearError: () => setError(null),
    pending,
    dismiss: () => setPending(null),
    choose,
    reviewUpgrade,
    reviewSeats,
    confirm,
    openPortal,
  };
}
